/**
 * Страница визарда заказа профрисков (`/orders/:id/profrisk`, гард company). Загружает каркас заказа
 * (OrderDetail) и анкету (ProfriskAnketa) и передаёт их в ProfriskWizard. Для submitted-заказа анкета
 * приходит с `canEdit:false` — визард открывается read-only. Не-профриск заказ — мягкая ошибка.
 */
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ordersApi, profriskApi, type OrderDetail, type ProfriskAnketa } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { serviceLabel } from '@/lib/orders'
import { ProfriskWizard } from '@/profrisk/profrisk-wizard'
import { useAuth } from '@/app/auth-context'
import { AnketaCancelOrderButton, CancelledOrderAlert } from '@/orders/order-cancel'
import { OrderQuestionButton } from '@/questions/ask-question'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

export function OrderProfriskWizardPage() {
  const navigate = useNavigate()
  const { refresh } = useAuth()
  // Анкета отправлена прямо сейчас — отменять уже со страницы заказа, не с экрана «Отправлено».
  const [sent, setSent] = useState(false)
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  /**
   * Предупреждение о неудачном автодобавлении должностей при заказе с выбранными рабочими
   * местами со страницы «Профриски» (centrika-docs!35). Снимается РОВНО ОДИН РАЗ при
   * монтировании (ленивый инициализатор `useState`), а не читается из `location.state` на
   * каждом рендере — так же, как в мастере СОУТ: любая замена записи истории обнулила бы
   * `state`, и предупреждение исчезло бы вскоре после появления.
   */
  const [bulkAddWarning] = useState(
    () => (location.state as { bulkAddWarning?: string } | null)?.bulkAddWarning,
  )
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [anketa, setAnketa] = useState<ProfriskAnketa | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    // Сначала каркас заказа — чтобы проверить услугу ДО запроса анкеты: для не-profrisk заказа
    // getAnketa вернул бы 404 и увёл бы в общий error, минуя осмысленное сообщение «не тот сервис».
    ordersApi
      .get(id, controller.signal)
      .then(async (ord) => {
        if (controller.signal.aborted) return
        setOrder(ord)
        if (ord.service !== 'profrisk') return
        const ank = await profriskApi.getAnketa(id, controller.signal)
        if (controller.signal.aborted) return
        setAnketa(ank)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id])

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/orders')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К моим заказам
        </Button>
      </div>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !order ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : order.service !== 'profrisk' ? (
        <Alert variant="danger">
          Это заказ услуги «{serviceLabel(order.service)}» — визард профрисков к нему не применим.
        </Alert>
      ) : !anketa ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <PageHead
            title="Заказ профрисков"
            subtitle="Заполните анкету оценки профрисков по шагам — черновик сохраняется автоматически"
            actions={
              sent ? undefined : (
                <div className="flex flex-wrap justify-end gap-2">
                  {/* Вопрос по заказу (client-questions; ТЗ п.9.7): у заказа «На заполнении» и
                      «Требуются правки» анкета и есть страница заказа. */}
                  <OrderQuestionButton order={order} onCreated={() => void refresh()} />
                  <AnketaCancelOrderButton order={order} onCancelled={() => void refresh()} />
                </div>
              )
            }
          />
          {order.dataStatus === 'changes_requested' && order.returnComment ? (
            <Alert variant="info" title="Требуются правки" className="mb-4">
              {order.returnComment}
            </Alert>
          ) : null}
          {order.dataStatus === 'cancelled' ? (
            <CancelledOrderAlert orderId={order.id} className="mb-4" />
          ) : null}
          {bulkAddWarning ? (
            <Alert variant="danger" title="Не всё добавлено автоматически" className="mb-4">
              {bulkAddWarning}
            </Alert>
          ) : null}
          <ProfriskWizard
            order={order}
            anketa={anketa}
            onSubmitted={() => {
              setSent(true)
              void refresh()
            }}
          />
        </>
      )}
    </div>
  )
}
